import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { CaptionsItem } from '../items/captions/captions-item-type';
import { SolidItem } from '../items/solid/solid-item-type';
import { TextItem } from '../items/text/text-item-type';
import { changeItemsWithSelection } from '../state/actions/change-items';
import { useWriteContext } from '../utils/use-context';
import { useTargetItemIds } from './utils/use-target-item-ids';
import { InspectorSubLabel } from './components/inspector-label';
import {
  ColorInputField,
  ColorPicker,
  ToolButton,
  ValueInputField,
} from '@crabs-ai/ds-react';
import { Remove } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { useSelectionValues } from './utils/use-selection-values';
import { useTextSelection } from '../contexts/text-selection-context';

// segments 내 색상이 혼합되어 있는지 확인
const MIXED_MARKER = '__MIXED__';

const getColorFromTextItem = (item: TextItem): string => {
  if (!item.segments || item.segments.length === 0) {
    return item.color;
  }

  // 모든 segment의 색상 수집 (없으면 item.color 사용)
  const colors = item.segments.map((seg) => seg.color ?? item.color);
  const firstColor = colors[0];
  const allSame = colors.every((c) => c === firstColor);

  return allSame ? firstColor! : MIXED_MARKER;
};

function parseHexAlphaColor(color?: string | null): {
  baseColor: string | null;
  opacity: number;
} {
  if (!color || color === 'transparent') {
    return { baseColor: null, opacity: 1 };
  }
  if (!color.startsWith('#')) {
    return { baseColor: color, opacity: 1 };
  }
  const hex = color.slice(1);
  if (hex.length === 8) {
    const baseHex = `#${hex.slice(0, 6)}`;
    const alpha = parseInt(hex.slice(6, 8), 16);
    return { baseColor: baseHex, opacity: alpha / 255 };
  }
  return { baseColor: color, opacity: 1 };
}

function mergeHexAlphaColor(baseColor: string, opacity: number): string {
  if (!baseColor.startsWith('#')) return baseColor;
  const baseHex = baseColor.slice(1, 7);
  if (opacity >= 1) return `#${baseHex}`;
  const alphaHex = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${baseHex}${alphaHex}`;
}

const ColorInspectorUnmemoized: React.FC<{
  color?: string | null;
  itemId: string;
  colorType: 'color' | 'highlightColor' | 'strokeColor';
  accessibilityLabel: string;
  disableCharacterStyleEditing?: boolean;
  onRemove?: () => void;
  /** 아이템 불투명도 (0-1) - 제공 시 ColorPicker와 동기화되어 캔버스에 반영 */
  itemOpacity?: number;
}> = ({
  color,
  itemId,
  colorType,
  accessibilityLabel,
  disableCharacterStyleEditing = false,
  onRemove,
  itemOpacity,
}) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { selectionStyle, applyColor, applyOpacity, applyStrokeColor } =
    useTextSelection();

  // Popover 열릴 때 선택 상태 저장
  const hadSelectionOnOpenRef = useRef(false);

  // 선택 상태 (disableCharacterStyleEditing이 true면 항상 false)
  const effectiveHasSelection = disableCharacterStyleEditing
    ? false
    : selectionStyle.hasSelection;
  const isSelectionAwareColorType =
    colorType === 'color' || colorType === 'strokeColor';

  const selectColor = useCallback(
    (item: TextItem | SolidItem | CaptionsItem) => {
      if (colorType === 'highlightColor') {
        if (item.type !== 'captions') {
          throw new Error(
            'Highlight color is only available for captions items',
          );
        }
        return item.highlightColor;
      }

      if (colorType === 'strokeColor') {
        if (
          item.type !== 'text' &&
          item.type !== 'captions' &&
          item.type !== 'solid'
        ) {
          throw new Error(
            'Stroke color is only available for text, captions, or solid items',
          );
        }

        return (item as TextItem | CaptionsItem).strokeColor ?? 'transparent';
      }

      if (item.type === 'text') {
        // TextItem은 segments 고려
        return getColorFromTextItem(item);
      }

      if (item.type === 'solid' || item.type === 'captions') {
        return item.color;
      }

      throw new Error(
        'Invalid item type for color inspector: ' + JSON.stringify(item),
      );
    },
    [colorType],
  );

  const { isMixed: isMultiItemMixed, value: selectedColor } =
    useSelectionValues<
      TextItem | SolidItem | CaptionsItem,
      string | null | undefined
    >(itemId, selectColor);

  // 선택 영역 내 Mixed 마커
  const SELECTION_MIXED = '__SELECTION_MIXED__';

  // 최종 Mixed 여부 결정
  // 1. 편집 모드에서 텍스트 선택 시 → selectionStyle.color가 Mixed 마커인지 확인
  // 2. 그 외 → 여러 아이템 혼합 또는 segments 내 혼합
  const selectionColorValue =
    colorType === 'strokeColor'
      ? selectionStyle.strokeColor
      : selectionStyle.color;

  const isMixed =
    effectiveHasSelection && isSelectionAwareColorType
      ? selectionColorValue === SELECTION_MIXED
      : isMultiItemMixed || selectedColor === MIXED_MARKER;

  // 표시할 색상 결정
  // 1. 편집 모드에서 텍스트 선택 시 → selectionStyle.color (Mixed면 color prop 사용)
  // 2. 그 외 → item.color
  const displayColor =
    effectiveHasSelection && isSelectionAwareColorType
      ? selectionColorValue === SELECTION_MIXED
        ? color
        : (selectionColorValue ?? color)
      : color;

  const hasColor =
    displayColor != null &&
    displayColor !== '' &&
    displayColor !== 'transparent';
  const parsedPickerColor = useMemo(
    () => parseHexAlphaColor(displayColor),
    [displayColor],
  );
  const isColorOpacityControlled =
    colorType === 'color' ||
    colorType === 'strokeColor' ||
    colorType === 'highlightColor';
  const isNoneColor = isColorOpacityControlled && !hasColor && !isMixed;

  // Opacity: itemOpacity prop이 있으면 아이템 상태와 동기화, 없으면 내부 state
  const [internalOpacity, setInternalOpacity] = useState(1);
  const opacity = isColorOpacityControlled
    ? parsedPickerColor.opacity
    : (itemOpacity ?? internalOpacity);

  const handleOpacityChange = useCallback(
    (newOpacity: number, commitToUndoStack = true) => {
      if (Math.abs(newOpacity - opacity) <= 0.0001) {
        return;
      }

      if (hadSelectionOnOpenRef.current && colorType === 'color') {
        applyOpacity(newOpacity);
        return;
      }

      if (hadSelectionOnOpenRef.current && colorType === 'strokeColor') {
        if (!parsedPickerColor.baseColor) return;
        applyStrokeColor(
          mergeHexAlphaColor(parsedPickerColor.baseColor, newOpacity),
        );
        return;
      }

      if (isColorOpacityControlled) {
        if (!parsedPickerColor.baseColor) return;
        const nextColor = mergeHexAlphaColor(
          parsedPickerColor.baseColor,
          newOpacity,
        );
        setState({
          update: (state) =>
            changeItemsWithSelection(state, itemId, targetIds, (i) => ({
              ...i,
              [colorType]: nextColor,
            })),
          commitToUndoStack,
        });
        return;
      }

      if (itemOpacity !== undefined) {
        setState({
          update: (state) =>
            changeItemsWithSelection(state, itemId, targetIds, (i) => {
              if (i.opacity === newOpacity) return i;
              return { ...i, opacity: newOpacity };
            }),
          commitToUndoStack,
        });
      } else {
        setInternalOpacity(newOpacity);
      }
    },
    [
      isColorOpacityControlled,
      colorType,
      itemOpacity,
      setState,
      itemId,
      targetIds,
      parsedPickerColor.baseColor,
      opacity,
      applyOpacity,
      applyStrokeColor,
      isSelectionAwareColorType,
    ],
  );

  const onColorChange = useCallback(
    (newColor: string | null, commitToUndoStack = true) => {
      if (hadSelectionOnOpenRef.current && colorType === 'color') {
        applyColor(newColor ?? 'transparent');
        return;
      }

      if (hadSelectionOnOpenRef.current && colorType === 'strokeColor') {
        applyStrokeColor(newColor ?? 'transparent');
        return;
      }

      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (colorType === 'highlightColor') {
              if (i.type !== 'captions') {
                throw new Error('Item is not a captions');
              }
              return {
                ...(i as CaptionsItem),
                highlightColor: newColor,
              };
            }

            if (colorType === 'strokeColor') {
              if (
                i.type !== 'text' &&
                i.type !== 'captions' &&
                i.type !== 'solid'
              ) {
                throw new Error('Item is not a text, caption, or solid');
              }
              const updated = {
                ...(i as TextItem | CaptionsItem | SolidItem),
                strokeColor: newColor ?? 'transparent',
              };
              if (i.type === 'text' && (i as TextItem).segments) {
                const textItem = updated as TextItem;
                const newSegments = textItem.segments?.map((seg) => {
                  const { strokeColor: _strokeColor, ...rest } = seg;
                  return rest;
                });
                const hasStyledSegments = newSegments?.some(
                  (s) =>
                    s.color ||
                    s.fontFamily ||
                    s.fontWeight ||
                    s.fontVariant ||
                    s.fontSize ||
                    s.opacity !== undefined,
                );
                return {
                  ...textItem,
                  segments: hasStyledSegments ? newSegments : undefined,
                };
              }
              return updated;
            }

            if (
              i.type === 'text' ||
              i.type === 'solid' ||
              i.type === 'captions'
            ) {
              const updated = {
                ...(i as TextItem | SolidItem | CaptionsItem),
                color: newColor ?? 'transparent',
              };
              if (i.type === 'text' && (i as TextItem).segments) {
                const textItem = updated as TextItem;
                const newSegments = textItem.segments?.map((seg) => {
                  const { color: _, ...rest } = seg;
                  return rest;
                });
                const hasStyledSegments = newSegments?.some(
                  (s) =>
                    s.fontFamily ||
                    s.strokeColor ||
                    s.fontWeight ||
                    s.fontVariant ||
                    s.fontSize ||
                    s.opacity !== undefined,
                );
                return {
                  ...textItem,
                  segments: hasStyledSegments ? newSegments : undefined,
                };
              }
              return updated;
            }
            throw new Error('Invalid item type: ' + JSON.stringify(i));
          });
        },
        commitToUndoStack,
      });
    },
    [
      setState,
      itemId,
      colorType,
      targetIds,
      applyColor,
      applyStrokeColor,
      isSelectionAwareColorType,
    ],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        // disableCharacterStyleEditing이 true면 선택 상태를 무시
        hadSelectionOnOpenRef.current =
          hadSelectionOnOpenRef.current ||
          (effectiveHasSelection && isSelectionAwareColorType);
      }
    },
    [effectiveHasSelection, isSelectionAwareColorType],
  );

  const handleDropdownFocusOutside = useCallback(
    (e: Event) => {
      if (!effectiveHasSelection || !isSelectionAwareColorType) {
        return;
      }

      e.preventDefault();
    },
    [effectiveHasSelection, isSelectionAwareColorType],
  );

  return (
    <div className="flex-1">
      <InspectorSubLabel>
        {colorType === 'color' || colorType === 'strokeColor'
          ? t('Editor.inspector.text.default-color')
          : t('Editor.inspector.text.highlight-color')}
      </InspectorSubLabel>
      <div className="flex items-center gap-4xs">
        <div className="min-w-0 flex-1">
          <ColorInputField
            type={isNoneColor ? 'none' : 'default'}
            value={
              isMixed
                ? 'Mixed'
                : hasColor
                  ? colorType === 'strokeColor' ||
                    colorType === 'color' ||
                    colorType === 'highlightColor'
                    ? (parsedPickerColor.baseColor ?? '')
                    : (displayColor ?? '')
                  : ''
            }
            accessibilityLabel={accessibilityLabel}
            hasMixedValue={isMixed}
            mixedLabel="Mixed"
            onOpenChange={handleOpenChange}
            onTriggerMouseDown={(e) => {
              hadSelectionOnOpenRef.current =
                effectiveHasSelection && isSelectionAwareColorType;
              if (effectiveHasSelection && isSelectionAwareColorType) {
                e.preventDefault();
              }
            }}
            onDropdownFocusOutside={handleDropdownFocusOutside}
            keepOpenOnContentPointerDown={
              effectiveHasSelection && isSelectionAwareColorType
            }
            dropdownContent={
              <ColorPicker
                value={
                  hasColor
                    ? colorType === 'strokeColor' ||
                      colorType === 'color' ||
                      colorType === 'highlightColor'
                      ? parsedPickerColor.baseColor
                      : (displayColor ?? null)
                    : null
                }
                onChange={(nextValue) => {
                  if (colorType === 'highlightColor') {
                    if (!nextValue) {
                      onColorChange(null, false);
                      return;
                    }
                    onColorChange(
                      mergeHexAlphaColor(nextValue, opacity),
                      false,
                    );
                    return;
                  }
                  if (colorType === 'strokeColor' || colorType === 'color') {
                    if (!nextValue) {
                      onColorChange('transparent', false);
                      return;
                    }
                    onColorChange(
                      mergeHexAlphaColor(nextValue, opacity),
                      false,
                    );
                    return;
                  }
                  onColorChange(nextValue ?? 'transparent', false);
                }}
                onChangeEnd={(nextValue) => {
                  if (colorType === 'highlightColor') {
                    if (!nextValue) {
                      onColorChange(null, true);
                      return;
                    }
                    onColorChange(mergeHexAlphaColor(nextValue, opacity), true);
                    return;
                  }
                  if (colorType === 'strokeColor' || colorType === 'color') {
                    if (!nextValue) {
                      onColorChange('transparent', true);
                      return;
                    }
                    onColorChange(mergeHexAlphaColor(nextValue, opacity), true);
                    return;
                  }
                  onColorChange(nextValue ?? 'transparent', true);
                }}
                opacity={opacity}
                onOpacityChange={(newOpacity) =>
                  handleOpacityChange(newOpacity, false)
                }
                onOpacityChangeEnd={(newOpacity) =>
                  handleOpacityChange(newOpacity, true)
                }
              >
                <ColorPicker.ModeSwitch />
                <ColorPicker.Area />
                <ColorPicker.Input />
                <ColorPicker.Presets />
              </ColorPicker>
            }
          />
        </div>
        {isColorOpacityControlled && (
          <div className="w-[96px] shrink-0">
            <ValueInputField
              value={Math.round(opacity * 100)}
              isShowLabel={false}
              onChange={(v) => {
                const num = typeof v === 'string' ? Number.parseInt(v, 10) : v;
                if (Number.isNaN(num)) return;
                handleOpacityChange(Math.max(0, Math.min(100, num)) / 100);
              }}
              isUnit="%"
              min={0}
              max={100}
              step={1}
              accessibilityLabel={`${accessibilityLabel} opacity`}
              disabled={isNoneColor}
            />
          </div>
        )}
        {onRemove && (
          <ToolButton
            aria-label="Remove highlight color"
            onClick={onRemove}
            state={!hasColor ? 'disabled' : 'default'}
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.remove-highlight-color')}
            className="h-[36px] w-[36px] shrink-0 text-sys-cont-neutral-default"
          >
            <Remove className="size-4" />
          </ToolButton>
        )}
      </div>
    </div>
  );
};

export const ColorInspector = memo(ColorInspectorUnmemoized);
